import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "react-loader-spinner";
import Button from "../../Button/Button";
import "./style.css";
import { Helper } from "../../utils/Helper/HelperUtil";
import { isNullOrUndefined } from "../../utils/formatting";
import { PermissionsList } from "../../models/Permissions";

import {
  userChosenPropertyId,
  userChosenPropertyType,
  userChosenPropertyAddress,
  userChosenRepairLocationId,
  userChosenRepairLocation,
  userChosenHighLevelDescription as storeUserChosenHighLevelDescription,
  userChosenSupplementaryQuestions,
  userChosenDuplicateQuestion,
  userChosenAdditionalDescription,
  userChosenVandalismQuestion,
  userChosenVandalismAdditionalInformation,
  userChosenCovidQuestion,
  userChosenVulnerabilityQuestion,
  userChosenVulnerabilityAdditionalInformation,
  userChosenContactDetails,
  userChosenUPRN,
  userChosenSTATUS,
  userChosenEligibilityErrorCode,
  userChosenContactPhone,
  userChosenContactName,
  userChosenRelationDetails,
  backToRepairAsset,
  loadDuplicateRepairPage,
  userChosenResumeRepair,
  resumeDraftRepair,
  userResumeDraftDataCache,
  workOrderData,
  saveEmailCopyRadioValue,
  userChosenSOR,
  defaultAssetId,
  showButtonRequestAnotherRepair,
} from "../../ducks/redux/actions/userChoices";

import {
  getHeatingHotWaterCase,
  getMaintResp,
  getRepairResp,
  getContractorName,
  getDeterminedContractorDetails,
  getContractorPayload,
  workOrderRepairObject,
} from "../../ducks/redux/actions/getAddress";

import { finishJourney, removeAllDMCData } from "../../ducks/redux/actions/FinancialReferral";
import { WorksOrderRequest } from "../../models/WorksOrders/WorksOrderRequest";
import { userTypedRepairReasons } from "../../ducks/redux/actions/dashboardReducer.actions";
import { nanoid } from "nanoid";
import successIcon from "./icons/Multiple-WO-Success-Icon.png";
import failIcon from "./icons/Multiple-WO-Fail-Icon.png";
import partialIcon from "./icons/Multiple-WO-Partial-Icon.png";
import { isUndefinedNullOrEmpty } from "../../utils/translation/helper";

type Row = {
  referenceRow: { title: string; body: string };
  nextStepsRow: { title: string; body: string | string[] };
};

interface RepairFinalDetailsProps extends RouteComponentProps<any> { }

const RepairFinalDetails: React.FC<RepairFinalDetailsProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const workOrderObject = useSelector((state: any) => state?.addresses?.workOrderRepair);
  const dmcWorkOrderObject = useSelector((state: any) => state?.addresses?.dmcWorkOrderRepair);
  const woFailureCases = useSelector((state: any) => state?.userChoicesReducer?.woFailureCases);
  const hasExistingDMCaseOrWO = useSelector((state: any) => state?.userChoicesReducer?.hasExistingDMCaseOrWO);
  const appointmentBundle = useSelector((state: any) => state?.userChoicesReducer?.appointmentBundle);
  const ButtonRequestAnotherRepairValue = useSelector((state: any) => state?.userChoicesReducer?.ButtonRequestAnotherRepair);
  const repairHistoryRedirectPage = useSelector((state: any) => state?.userChoicesReducer?.repairHistoryRedirectPage);

  const getPermissionsData = useSelector((state: any) => state?.userChoicesReducer?.userProfilePermissions);
  const hasViewContractorOOHCommunicationInstruction = Array.isArray(getPermissionsData) && getPermissionsData.indexOf(PermissionsList.view_contractor_OOH_communication_instruction) >= 0;


  const [rows, setRows] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(true);
  const rowKeyMap = useRef<WeakMap<Row, string>>(new WeakMap());
  const getRowKey = (r: Row) => {
    let k = rowKeyMap.current.get(r);
    if (!k) { k = nanoid(); rowKeyMap.current.set(r, k); }
    return k;
  };
  const getLineKey = (r: Row, line: string) => `${getRowKey(r)}::${line}`;

  const resetCommonState = () => {
    dispatch(saveEmailCopyRadioValue(""));
    dispatch(removeAllDMCData(null));
    dispatch(storeUserChosenHighLevelDescription(""));
    dispatch(userChosenSupplementaryQuestions(""));
    dispatch(userChosenDuplicateQuestion(""));
    dispatch(userChosenAdditionalDescription(""));
    dispatch(userChosenVandalismQuestion("No"));
    dispatch(userChosenVandalismAdditionalInformation(""));
    dispatch(userChosenCovidQuestion(""));
    dispatch(userChosenVulnerabilityQuestion(""));
    dispatch(userChosenVulnerabilityAdditionalInformation(""));
    dispatch(userChosenContactDetails(""));
    dispatch(userChosenUPRN(""));
    dispatch(userChosenSOR(""));
    dispatch(getRepairResp(""));
    dispatch(getHeatingHotWaterCase(""));
    dispatch(getMaintResp(""));
    dispatch(getDeterminedContractorDetails(""));
    dispatch(getContractorName(""));
    dispatch(getContractorPayload(""));
    dispatch(userChosenSTATUS(""));
    dispatch(userChosenEligibilityErrorCode(""));
    dispatch(userChosenContactPhone(""));
    dispatch(userChosenContactName(""));
    dispatch(userChosenRelationDetails(""));
    dispatch(loadDuplicateRepairPage(false));
  };

  const callPageHandler = async () => {
    setIsLoading(true);
    setButtonsDisabled(true);
    try {
      resetCommonState();
      dispatch(finishJourney(true));
      dispatch(userChosenPropertyId(""));
      dispatch(userChosenPropertyType(""));
      dispatch(userChosenPropertyAddress(""));
      dispatch(backToRepairAsset(false));
      dispatch(userChosenResumeRepair(false));
      const worksOrder = new WorksOrderRequest(workOrderObject);
      dispatch(workOrderData(worksOrder));
      if (isNullOrUndefined(worksOrder?.identifiers?.worksOrderIdentifier)) {
        props.history.push("/GenericError");
      }
      else {
        dispatch(showButtonRequestAnotherRepair(false))
        props.history.push(`/WorkOrder360View/${worksOrder?.identifiers?.worksOrderIdentifier}`);
      }
    } catch {
      props.history.push("/GenericError");
    } finally {
      setIsLoading(false);
      setButtonsDisabled(false);
    }
  };

  const callRepairHistoryHandler = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    dispatch(defaultAssetId(workOrderObject?.premisesId));
    if (!isUndefinedNullOrEmpty(repairHistoryRedirectPage)) {
      dispatch(showButtonRequestAnotherRepair(false))
      props.history.push(repairHistoryRedirectPage);
    }
    else {
      dispatch(showButtonRequestAnotherRepair(false))
      props.history.push("/AssetWorkOrderResult");
    }

  };

  const callFinishCreatePageHandler = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setButtonsDisabled(true);
    try {
      resetCommonState();
      dispatch(workOrderRepairObject(null));
      dispatch(resumeDraftRepair(false));
      dispatch(userResumeDraftDataCache(null));
      dispatch(userChosenPropertyAddress(""));
      dispatch(userChosenRepairLocationId(0));
      dispatch(userChosenRepairLocation(""));
      dispatch(userTypedRepairReasons(""));
      dispatch(userChosenPropertyId(""));
      dispatch(userChosenResumeRepair(false));
      props.history.push("/RepairAsset", { previousPathname: window.location.pathname });
    } catch {
      props.history.push("/GenericError");
    } finally {
      setIsLoading(false);
      setButtonsDisabled(false);
    }
  };

  const loadFinalDetails = async () => {
    let mounted = true;
    setIsLoading(true);
    setButtonsDisabled(true);
    try {
      const result: Row[] = Helper.buildRepairJourneyConclusionMsgs({
        workOrderObject,
        dmcWorkOrderObject,
        woFailureCases,
        props,
        hasExistingDMCaseOrWO,
        t,
        appointmentBundle,
        hasViewContractorOOHCommunicationInstruction,
        ButtonRequestAnotherRepairValue
      });
      if (mounted) setRows(Array.isArray(result) ? result : []);
      return () => {
        mounted = false;
      };
    } finally {
      if (mounted) {
        setIsLoading(false);
        setButtonsDisabled(false);
      }
    }
  }

  useEffect(() => {
    loadFinalDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type SummaryCase = ReturnType<typeof Helper.getRepairJourneySummaryCase>;

  const summaryCase: SummaryCase = Helper.getRepairJourneySummaryCase({
    workOrderObject,
    dmcWorkOrderObject,
    woFailureCases,
    ButtonRequestAnotherRepairValue
  });

  const summaryDetails = Helper.getRepairJourneySummary(summaryCase, {
    successIcon,
    failIcon,
    partialIcon,
  });


  return (
    <div className="container sm:px-2 height-container" role="main">
      {isLoading ? (
        <>
          <Loader type="ThreeDots" color="#00BFFF" height={50} width={50} />
          <p className="global-txt mt-4">{t("CPR052")}</p>
        </>
      ) : (
        <>
          <div id="repairfinal-main-div" className="h5 my-3 repair-text">
            <div className="rf-summary">
              <img src={summaryDetails.icon} alt="" className="rf-summary__icon" />
              <h2 id="repairfinal-h1" className="text-purple-100 text-2xl mb-1 global-txt rf-summary__title">
                {t(summaryDetails.headingKey)}
              </h2>
            </div>
          </div>


          {summaryDetails.subKey && summaryDetails.subKey !== t(summaryDetails.subKey) &&
            <div id="repairfinal-div1" className="repair-text global-txt">
              <p id="repairfinal-p" className="rf-summary__sub">{t(summaryDetails.subKey)}<strong></strong></p>
              <p id="repairfinal-p2"></p>
            </div>
          }

          {rows.map((row) => (
            <div
              key={getRowKey(row)}
              className="border border-grey bg-grey rounded-lg mar-left global-txt mt-2 rf-card"
            >
              <div className="repair-text global-txt mt-2">
                <strong className="pl-4 block">{row.referenceRow.title}</strong>
                <span className="pl-2 block">{row.referenceRow.body}</span>
              </div>
              <br />
              <div className="pb-2">
                <strong className="pl-4">{row.nextStepsRow.title}</strong>
                <br />
                {Array.isArray(row.nextStepsRow.body) ? (
                  row.nextStepsRow.body.map((line) => (
                    <span key={getLineKey(row, line)} className="pl-2 block">{line}</span>
                  ))
                ) : (
                  <span className="pl-2 block">{row.nextStepsRow.body}</span>
                )}
              </div>
            </div>
          ))}

          <div className="rf-history global-txt">
            <strong className="rf-history__text ">{t("for_all_repair_details_visit")}</strong>{" "}
            <button data-testid="repair-history-link" type="button" onClick={callRepairHistoryHandler} className="rf-history__link">
              {t("Repair_history_Link")}
            </button>.
          </div>

          <div className="rf-actions">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            {!ButtonRequestAnotherRepairValue && <a
              id="repairfinal-btn1"
              data-testid="finishandcreate"
              href="#"
              onClick={callFinishCreatePageHandler}
              className="finish rf-actions__createAnotherRepair"
            >
              <span className="mr-2 ml-0 global-txt cursor-pointer sm:ml-neg">
                {t("request_another_repair")}
              </span>
            </a>}
            <Button
              id="btn_finish_repairfinal"
              data-testid="finshrepair"
              onClick={callPageHandler}
              className="button-class rf-actions__viewRepair"
              disabled={buttonsDisabled}
            >
              {t("view_repair_details")}
            </Button>

          </div>
        </>
      )}
    </div>
  );
};
export default RepairFinalDetails;